import { memo, useCallback } from 'react';
import classNames from 'classnames';
import { V2QsBloodPressureProps, V2QsBloodPressureValue } from './types';

/**
 * V2QsBloodPressure 컴포넌트
 *
 * 혈압 입력을 위한 컴포넌트 (수축기/이완기)
 * V1 QsBloodPressure의 V2 버전으로 순수 UI만 담당
 *
 * @example
 * ```tsx
 * <V2QsBloodPressure
 *   value={{ systolic: 120, diastolic: 80 }}
 *   onChange={setValue}
 *   systolicPlaceholder="수축기"
 *   diastolicPlaceholder="이완기"
 * />
 * ```
 */
function V2QsBloodPressure({
  value,
  onChange,
  systolicPlaceholder = 'Systolic',
  diastolicPlaceholder = 'Diastolic',
  error = false,
  errorMessage,
  disabled = false,
  readOnly = false,
  fullWidth = true,
  className,
  containerClassName,
  label,
  hint,
  unit = 'mmHg',
}: V2QsBloodPressureProps) {
  const { systolic = '', diastolic = '' } = value || {};

  // 수축기 변경 핸들러
  const handleSystolicChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      // 숫자만 허용
      if (inputValue !== '' && !/^\d*$/.test(inputValue)) return;

      onChange?.({
        systolic: inputValue,
        diastolic,
      });
    },
    [onChange, diastolic]
  );

  // 이완기 변경 핸들러
  const handleDiastolicChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      // 숫자만 허용
      if (inputValue !== '' && !/^\d*$/.test(inputValue)) return;

      onChange?.({
        systolic,
        diastolic: inputValue,
      });
    },
    [onChange, systolic]
  );

  const hasError = error;

  // Container 클래스
  const containerClasses = classNames('flex flex-col gap-1', containerClassName);

  // Input wrapper 클래스
  const inputWrapperClasses = classNames(
    'flex items-center transition-all duration-200 rounded-[10px]',
    'border-[1.5px] px-4',
    {
      // Width
      'w-full': fullWidth,
      'w-[280px]': !fullWidth,

      // Border color
      'border-[#E6E9F5]': !hasError && !disabled,
      'border-[#FF5E71]': hasError,
      'hover:border-[#DADDEB]': !disabled && !hasError && !readOnly,
      'focus-within:border-[#4B7BF5]': !disabled && !hasError,

      // Background
      'bg-white': !disabled,
      'bg-[#F2F4FC]': disabled,
    },
    className
  );

  // Input 클래스
  const inputClasses = classNames(
    'flex-1 min-w-0 py-[17px] bg-transparent outline-none',
    'text-[15px] font-medium leading-[1.6] text-center',
    {
      // Text color
      'text-[#1D1D1F]': !disabled,
      'text-[#AFB2C2]': disabled,

      // Placeholder
      'placeholder:text-[#AFB2C2]': true,

      // Cursor
      'cursor-not-allowed': disabled,
    }
  );

  // Separator 클래스
  const separatorClasses = 'text-2xl font-light text-[#AFB2C2] px-2';

  // Unit 클래스
  const unitClasses = 'text-[14px] text-[#AFB2C2] ml-2';

  // 라벨 클래스
  const labelClasses = 'text-sm font-medium text-[#222638] mb-1';

  // 힌트 클래스
  const hintClasses = 'text-xs text-[#AFB2C2]';

  // 에러 메시지 클래스
  const errorClasses = 'text-xs text-[#FF5E71]';

  return (
    <div className={containerClasses}>
      {/* Label */}
      {label && <label className={labelClasses}>{label}</label>}

      {/* Input Wrapper */}
      <div className={inputWrapperClasses}>
        {/* Systolic Input */}
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={systolic}
          onChange={handleSystolicChange}
          placeholder={systolicPlaceholder}
          disabled={disabled}
          readOnly={readOnly}
          className={inputClasses}
          maxLength={3}
        />

        {/* Separator */}
        <span className={separatorClasses}>/</span>

        {/* Diastolic Input */}
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={diastolic}
          onChange={handleDiastolicChange}
          placeholder={diastolicPlaceholder}
          disabled={disabled}
          readOnly={readOnly}
          className={inputClasses}
          maxLength={3}
        />

        {/* Unit */}
        {unit && <span className={unitClasses}>{unit}</span>}
      </div>

      {/* Hint */}
      {hint && !hasError && <span className={hintClasses}>{hint}</span>}

      {/* Error Message */}
      {hasError && errorMessage && (
        <span className={errorClasses}>{errorMessage}</span>
      )}
    </div>
  );
}

export default memo(V2QsBloodPressure);
